<template>
  <div class="viva-form-page">
    <!-- Header -->
    <div class="viva-page-header">
      <div class="viva-header-content">
        <img src="/viva/viva_logo.svg" alt="Viva" class="viva-logo" />
      </div>
      <ProfileDropdown
        :student-name="instructorName"
        current-portal="instructor"
        :disable-portal-link="!isVivaAdmin"
      />
    </div>

    <!-- Page Title Section -->
    <div class="instructor-info">
      <h1 class="viva-page-title">
        {{ isNewAssignment ? 'CREATE' : 'EDIT' }} ASSIGNMENT<span v-if="course.title" class="course-info-text"> for {{ course.title }}<span v-if="course.term || course.year" class="course-info-text"> {{ course.term }} {{ course.year }}</span></span>
      </h1>
    </div>

    <!-- Form Container -->
    <div class="viva-form-container">
      <!-- Loading state -->
      <div v-if="loading" class="viva-form-section viva-form-section-orange">
        <p>Loading assignment...</p>
      </div>

      <!-- Form -->
      <div v-else class="viva-form-section viva-form-section-orange">
        <div class="form-two-column">
          <!-- Left Column -->
          <div class="form-left">
            <!-- Assignment Title -->
            <div class="form-group-inline">
              <label class="form-label-inline">
                TITLE:
              </label>
              <div class="form-input-wrapper">
                <input
                  type="text"
                  class="form-input"
                  :class="{ 'error': errors.title }"
                  v-model="assignment.title"
                  placeholder="Enter assignment title"
                />
                <span class="required-asterisk">*</span>
                <div v-if="errors.title" class="error-message">{{ errors.title }}</div>
              </div>
            </div>

            <!-- Instructions -->
            <div class="form-group-inline">
              <div class="form-label-with-subtext">
                <label class="form-label-inline">INSTRUCTIONS:</label>
                <p class="form-label-subtext" @click="resetInstructions">
                  CLICK TO MAKE CHANGES TO DEFAULT INSTRUCTIONS:
                </p>
              </div>
              <div class="form-input-wrapper">
                <textarea
                  class="form-textarea"
                  v-model="assignment.instructions"
                  rows="8"
                ></textarea>
                <span class="required-asterisk">*</span>
              </div>
            </div>

            <!-- Due Date -->
            <div class="form-group-inline">
              <label class="form-label-inline">DUE DATE:</label>
              <div class="form-input-wrapper">
                <input
                  type="datetime-local"
                  class="form-input"
                  v-model="dueDateLocal"
                  :class="{ 'error': errors.due_date }"
                />
                <div v-if="errors.due_date" class="error-message">{{ errors.due_date }}</div>
              </div>
            </div>

            <!-- Upload Mode Toggle -->
            <div class="form-group-inline">
              <label class="form-label-inline">TYPE:</label>
              <div class="form-input-wrapper">
                <div class="upload-toggle-switch">
                  <div class="col-sm-5 text-center point"
                       :style="{color: uploadMode === 'student' ? 'var(--viva-orange)' : 'var(--viva-grey)'}"
                       @click="!assignment.is_sent && (uploadMode = 'student')">
                    Writing
                    <br>
                    (Students will upload their original work)
                  </div>
                  <div class="col-sm-2 text-center point" style="padding-left:0px;padding-right:0px;">
                    <label class="switch">
                      <input type="checkbox"
                             :checked="uploadMode === 'teacher'"
                             @change="uploadMode = $event.target.checked ? 'teacher' : 'student'"
                             :disabled="assignment.is_sent">
                      <span class="slider round"></span>
                    </label>
                  </div>
                  <div class="col-sm-5 text-center point"
                       :style="{color: uploadMode === 'teacher' ? 'var(--viva-green)' : 'var(--viva-grey)', paddingTop:'8px'}"
                       @click="!assignment.is_sent && (uploadMode = 'teacher')">
                    Reading
                    <br>
                    (I will upload a class assignment)
                  </div>
                </div>

                <!-- DragUpload appears when teacher upload selected -->
                <div v-if="uploadMode === 'teacher'" class="teacher-upload-section">
                  <div v-if="uploadState === 'uploading'" class="upload-progress">
                    <i class="fa fa-spinner fa-spin"></i> Uploading...
                  </div>
                  <div v-else-if="assignedFileInfo.fileUrl" class="uploaded-file-display">
                    <i class="fa fa-file-text-o"></i>
                    <span>{{ assignedFileName }}</span>
                    <a :href="serverAddr + assignedFileInfo.fileUrl"
                       target="_blank"
                       class="download-file-btn"
                       title="Download to verify">
                      <i class="fa fa-download"></i>
                    </a>
                    <button
                      v-if="!assignment.is_sent"
                      type="button"
                      class="remove-file-btn"
                      @click="removeAssignedFile"
                    >
                      &times;
                    </button>
                  </div>
                  <DragUpload
                    v-else-if="!assignment.is_sent"
                    v-on:fileurlchange="handleTeacherFileUpload"
                    :file-info="assignedFileInfo"
                    accept="application/pdf,image/jpeg,image/png,video/mp4,video/webm,video/quicktime"
                  ></DragUpload>
                  <p v-if="uploadError" class="upload-error">{{ uploadError }}</p>
                  <p v-if="errors.upload" class="upload-error">{{ errors.upload }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Right Column -->
          <div class="form-right">
            <div class="viva-type-selector">
              <h3>I want my VIVA to be: <span class="required-asterisk">*</span></h3>

              <div
                class="viva-type-option"
                :class="{ active: !isCustomized }"
                @click="!assignment.is_sent && setStandard()"
                :style="{ cursor: assignment.is_sent ? 'default' : 'pointer' }"
              >
                <input type="checkbox" :checked="!isCustomized" :disabled="assignment.is_sent" @click.stop="!assignment.is_sent && setStandard()" />
                <div class="viva-type-content">
                  <p><strong>STANDARD</strong> 4-question VIVA where student uploads assignment and has 60 seconds to respond to each question.</p>
                </div>
              </div>

              <div class="or-divider-with-lines">
                <hr />
                <span>OR</span>
                <hr />
              </div>

              <div
                class="viva-type-option"
                :class="{ active: isCustomized }"
                @click="!assignment.is_sent && (isCustomized = true)"
                :style="{ cursor: assignment.is_sent ? 'default' : 'pointer' }"
              >
                <input type="checkbox" :checked="isCustomized" :disabled="assignment.is_sent" @click.stop="!assignment.is_sent && (isCustomized = true)" />
                <div class="viva-type-content">
                  <strong>CUSTOMIZED:</strong>
                </div>
              </div>

              <!-- Customized Options -->
              <div class="customized-options" :class="{ active: isCustomized }">
                <div class="custom-option-row">
                  <input
                    type="number"
                    class="custom-option-number-input"
                    v-model.number="numQuestions"
                    :disabled="!isCustomized || assignment.is_sent"
                  />
                  <div class="custom-option-label" :class="{ enabled: isCustomized }">
                    The total number of questions I want each student asked for this VIVA assignment
                  </div>
                </div>
                <div v-if="errors.numQuestions" class="error-message" style="margin-left: 27px;">{{ errors.numQuestions }}</div>

                <div class="custom-option-row">
                  <input
                    type="number"
                    class="custom-option-number-input"
                    v-model.number="answerTime"
                    min="5"
                    max="300"
                    :disabled="!isCustomized || assignment.is_sent"
                  />
                  <div class="custom-option-label" :class="{ enabled: isCustomized }">
                    Maximum allowed answer time per question (in seconds)
                  </div>
                </div>
                <div v-if="errors.answerTime" class="error-message" style="margin-left: 27px;">{{ errors.answerTime }}</div>

                <div class="custom-option-row">
                  <input
                    type="checkbox"
                    v-model="customFirstEnabled"
                    :disabled="!isCustomized || assignment.is_sent"
                  />
                  <label class="custom-option-label" :class="{ enabled: isCustomized }">I want everyone asked the same first question, which is:</label>
                </div>
                <input
                  v-if="customFirstEnabled && isCustomized"
                  type="text"
                  class="form-input custom-question-input"
                  v-model="customFirstQuestion"
                  :disabled="assignment.is_sent"
                  placeholder="Enter first question"
                  :class="{ 'error': errors.customFirstQuestion }"
                />
                <div v-if="errors.customFirstQuestion" class="error-message" style="margin-left: 27px;">{{ errors.customFirstQuestion }}</div>

                <div class="custom-option-row">
                  <input
                    type="checkbox"
                    v-model="customLastEnabled"
                    :disabled="!isCustomized || assignment.is_sent"
                  />
                  <label class="custom-option-label" :class="{ enabled: isCustomized }">I want everyone asked the same last question, which is:</label>
                </div>
                <input
                  v-if="customLastEnabled && isCustomized"
                  type="text"
                  class="form-input custom-question-input"
                  v-model="customLastQuestion"
                  :disabled="assignment.is_sent"
                  placeholder="Enter last question"
                  :class="{ 'error': errors.customLastQuestion }"
                />
                <div v-if="errors.customLastQuestion" class="error-message" style="margin-left: 27px;">{{ errors.customLastQuestion }}</div>

                <p class="custom-option-label" :class="{ enabled: isCustomized }">
                  Other questions will be generated by the VIVA AI, which will be based on the uploaded document.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Button Group -->
        <div class="form-button-group">
          <button
            class="viva-btn viva-btn-orange viva-btn-small"
            @click="save(false)"
            v-if="assignment.is_sent"
            :disabled="saving"
          >
            {{ saving ? 'SAVING...' : 'SAVE' }}
          </button>
          <button
            class="viva-btn viva-btn-green viva-btn-small"
            @click="save(true)"
            v-if="!assignment.is_sent"
            :disabled="saving"
          >
            {{ saving ? 'SAVING...' : 'SAVE AND REVIEW STUDENT LIST' }}
          </button>
          <div v-if="assignment.is_sent" style="color: var(--viva-green); font-weight: 600; font-size: 16px;">
            ✓ Assignment has been sent to students
          </div>
        </div>
      </div>
    </div>

    <!-- Student List Modal -->
    <div v-if="showStudentModal" class="viva-modal-backdrop" @click.self="closeStudentModal">
      <div class="viva-modal-content student-list-modal">
        <div class="viva-modal-header">
          <h2>Review and Edit Student List</h2>
        </div>
        <div class="viva-modal-body">
          <p style="margin-bottom: 15px; color: var(--viva-grey);">
            Review and edit the student list for this course. Set accommodations before sending.
          </p>

          <!-- Bulk Add Section -->
          <div class="bulk-add-section">
            <button
              class="viva-btn viva-btn-outline viva-btn-small"
              @click="showBulkAddInput = !showBulkAddInput"
            >
              {{ showBulkAddInput ? 'Hide Add Students' : '+ Add Students' }}
            </button>

            <div v-if="showBulkAddInput" class="bulk-add-form">
              <textarea
                class="form-textarea"
                v-model="bulkAddEmails"
                placeholder="Paste emails separated by commas, spaces, or new lines"
                rows="4"
              ></textarea>
              <button
                class="viva-btn viva-btn-green viva-btn-small"
                @click="addBulkStudents"
                :disabled="!bulkAddEmails.trim()"
              >
                Add Students
              </button>
            </div>
          </div>

          <!-- Student Table -->
          <div class="student-table-wrapper">
            <table class="student-list-table">
              <thead>
                <tr>
                  <th class="email-col">EMAIL ADDRESS</th>
                  <th class="accommodation-col">TEXT QUESTIONS</th>
                  <th class="accommodation-col">EXTRA TIME</th>
                  <th class="action-col">REMOVE</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(student, index) in studentList" :key="index">
                  <td class="email-col">
                    <input
                      v-if="student.isNew"
                      type="email"
                      class="form-input form-input-small"
                      v-model="student.email"
                      placeholder="Enter email"
                    />
                    <span v-else>{{ student.email }}</span>
                  </td>
                  <td class="accommodation-col">
                    <label class="toggle-switch">
                      <input
                        type="checkbox"
                        v-model="student.text_questions_enabled"
                      />
                      <span class="toggle-label">{{ student.text_questions_enabled ? 'ON' : 'OFF' }}</span>
                    </label>
                  </td>
                  <td class="accommodation-col">
                    <button
                      @click="toggleExtraTime(index)"
                      :class="['accommodation-btn', student.time_multiplier > 1 ? 'active' : '']"
                      title="Toggle extra time (1.5x)"
                    >
                      {{ student.time_multiplier > 1 ? '1.5x' : 'OFF' }}
                    </button>
                  </td>
                  <td class="action-col">
                    <button
                      class="remove-btn"
                      @click="removeStudent(index)"
                      title="Remove student"
                    >
                      &times;
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>

            <div v-if="studentList.length === 0" class="empty-table-message">
              <p>No students added yet. Use "Add Students" to add students.</p>
            </div>
          </div>

          <p class="student-count">
            {{ studentList.length }} student{{ studentList.length !== 1 ? 's' : '' }} in list
          </p>
        </div>
        <div class="viva-modal-footer">
          <label class="notify-students-label">
            <input type="checkbox" v-model="notifyStudents" />
            Send email notifications to students
          </label>
          <button class="viva-btn viva-btn-gray viva-btn-small" @click="closeStudentModal">
            CANCEL
          </button>
          <button
            class="viva-btn viva-btn-green viva-btn-small"
            @click="saveStudentListAndSend"
            :disabled="sending || studentList.length === 0"
          >
            {{ sending ? 'SENDING...' : 'SAVE & SEND VIVA TO STUDENTS' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Back Link -->
    <div class="back-section">
      <a href="#" @click.prevent="goBack" class="back-link">
        <span>←</span> Back to Assignments
      </a>
    </div>
  </div>
</template>

<script>
import apiClient from '../utils/apiClient.js';
import { handleApiError } from '../utils/errorHandler.js';
import DragUpload from '../components/DragUpload.vue';
import { setEnv, ivChunkUploadMixin } from 'iv-base-components';
import env from '../utils/env.js';
import ProfileDropdown from '../components/ProfileDropdown.vue';
import { getStatusData } from '../utils/statusCache.js';

const DEFAULT_INSTRUCTIONS = 'Your instructor has requested that you take a brief, live-recorded oral response, during which you will upload your completed assignment and answer a series of questions about your work.';

const DEFAULT_INSTRUCTIONS_TEACHER_UPLOAD = 'Your instructor has requested that you take a brief, live-recorded oral response, during which you will answer a series of questions about the assigned materials.';

export default {
  name: 'AssignmentForm',
  mixins: [ivChunkUploadMixin],
  components: {
    DragUpload,
    ProfileDropdown,
  },

  data() {
    return {
      instructorName: '',
      isVivaAdmin: false,
      assignment: {
        id: null,
        course_id: null,
        title: '',
        instructions: DEFAULT_INSTRUCTIONS,
        due_date: null,
        is_sent: false,
        send_date: null
      },
      course: {
        title: '',
        term: '',
        year: ''
      },
      dueDateLocal: '',
      errors: {},
      isNewAssignment: true,
      courseId: null,
      loading: true,
      saving: false,
      showStudentModal: false,
      studentList: [],           // Array of {email, text_questions_enabled, time_multiplier, isNew}
      bulkAddEmails: '',         // Textarea for bulk adding emails
      showBulkAddInput: false,   // Toggle for bulk add textarea
      sending: false,
      notifyStudents: true,
      // Teacher upload fields
      uploadMode: 'student',     // 'student' | 'teacher'
      assignedFileInfo: {
        fileUrl: null,
        fileIsReadOnly: false,
        fileIsImage: false
      },
      assignedFileId: null,      // UID of uploaded file
      assignedFileName: '',      // Display name of uploaded file
      uploadState: 'initial',    // 'initial' | 'uploading' | 'complete'
      uploadProgress: 0,
      uploadError: null,
      serverAddr: env.serverAddr,
      // Concurrency
      originalUpdatedAt: null,
      // Customization
      isCustomized: false,
      numQuestions: 4,
      answerTime: 60,
      customFirstEnabled: false,
      customFirstQuestion: '',
      // Opt-out by default (Gloria wants the standard closing question to stay in more
      // custom VIVAs): checked and pre-filled, instructors uncheck or edit to remove it.
      customLastEnabled: true,
      customLastQuestion: "Is there anything else you'd like to say about this work?",
        // Also defined in shield/viva/models.py
      STANDARD_LAST: "Is there anything else you'd like to say about this work?",
    };
  },

  async mounted() {
    const statusData = getStatusData();
    if (statusData) {
      this.instructorName = statusData.student_name || '';
      this.isVivaAdmin = statusData.is_viva_admin || false;
    }
    // Configure iv-base-components environment for file uploads
    setEnv(env);

    const assignmentId = this.$route.params.id;
    this.courseId = this.$route.query.course_id;

    if (assignmentId !== 'new') {
      this.isNewAssignment = false;
      this.loading = true;

      try {
        const response = await apiClient.get(`/api/viva/instructor/assignment/${assignmentId}/`);

        if (response.data) {
          this.assignment = {
            id: response.data.id,
            course_id: response.data.course_id,
            title: response.data.title,
            instructions: response.data.instructions,
            due_date: response.data.due_date,
            is_sent: response.data.is_sent,
            send_date: response.data.send_date
          };

          this.courseId = response.data.course_id;

          // Convert UTC date from server to datetime-local format in user's timezone
          if (response.data.due_date) {
            const date = new Date(response.data.due_date);
            // Format as YYYY-MM-DDTHH:mm in local timezone for datetime-local input
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            this.dueDateLocal = `${year}-${month}-${day}T${hours}:${minutes}`;
          }

          // Store updated_at for concurrency check
          this.originalUpdatedAt = response.data.updated_at || null;

          // Load customization fields
          const nq = response.data.num_questions || 4;
          const at = response.data.answer_time || 60;
          const cfq = response.data.custom_first_question || '';
          const clq = response.data.custom_last_question || '';
          this.isCustomized = nq !== 4 || at !== 60 || !!cfq || (clq !== this.STANDARD_LAST);
          this.numQuestions = nq;
          this.answerTime = at;
          this.customFirstEnabled = !!cfq;
          this.customFirstQuestion = cfq;
          this.customLastEnabled = this.isCustomized && !!clq;
          this.customLastQuestion = clq;

          // Load assigned file info if present
          if (response.data.assigned_file_uid) {
            this.uploadMode = 'teacher';
            this.assignedFileId = response.data.assigned_file_uid;
            this.assignedFileName = 'Uploaded file';
            this.assignedFileInfo.fileUrl = response.data.assigned_file_url;
            this.uploadState = 'complete';
          }
        }

        // Load course details
        await this.loadCourse();
      } catch (error) {
        console.error('Failed to load assignment:', error);

        const errorMsg = handleApiError(error, this.$router, 'Failed to load assignment');
        if (error.response && error.response.status !== 401) {
          alert(errorMsg);
          this.$router.push({ name: 'instructor-dashboard' });
        }
      } finally {
        this.loading = false;
      }
    } else {
      // Set course_id for new assignment
      this.assignment.course_id = parseInt(this.courseId);
      // Load course details for new assignment
      await this.loadCourse();
      this.loading = false;
    }
  },

  methods: {
    async loadCourse() {
      if (!this.courseId) return;

      try {
        const response = await apiClient.get(`/api/viva/instructor/course/${this.courseId}/`);
        if (response.data) {
          this.course = {
            title: response.data.title || '',
            term: response.data.term || '',
            year: response.data.year || ''
          };
          // Use student_data if available (includes course-level accommodations)
          if (response.data.student_data && response.data.student_data.length > 0) {
            this.studentList = response.data.student_data.map(s => ({
              email: s.email,
              text_questions_enabled: s.text_questions_enabled || false,
              time_multiplier: parseFloat(s.time_multiplier) || 1.00,
              isNew: false
            }));
          } else {
            // Fall back to parsing emails with default accommodations
            const emailsStr = response.data.student_emails || '';
            this.studentList = this.parseEmailsToStudentList(emailsStr);
          }
        }
      } catch (error) {
        console.error('Failed to load course:', error);
      }
    },

    setStandard() {
      this.isCustomized = false;
      this.numQuestions = 4;
      this.answerTime = 60;
      this.customFirstEnabled = false;
      this.customFirstQuestion = '';
      this.customLastEnabled = true;
      this.customLastQuestion = this.STANDARD_LAST;
    },

    async save(reviewStudents) {
      // First save the assignment
      if (!this.validate()) {
        return;
      }

      this.saving = true;

      try {
        const assignmentData = {
          title: this.assignment.title,
          instructions: this.assignment.instructions,
          due_date: this.dueDateLocal ? new Date(this.dueDateLocal).toISOString() : null,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          send_now: false,
          num_questions: this.isCustomized ? this.numQuestions : 4,
          answer_time: this.isCustomized ? this.answerTime : 60,
          custom_first_question: (this.isCustomized && this.customFirstEnabled) ? this.customFirstQuestion : '',
          custom_last_question: this.isCustomized
            ? (this.customLastEnabled ? this.customLastQuestion : '')
            : this.STANDARD_LAST,
        };

        // Include assigned file if teacher upload mode and file is uploaded
        if (this.uploadMode === 'teacher' && this.assignedFileId) {
          assignmentData.assigned_file_uid = this.assignedFileId;
        } else if (this.uploadMode === 'student') {
          // Clear assigned file if switching to student upload mode
          assignmentData.assigned_file_uid = null;
        }

        if (this.isNewAssignment) {
          const response = await apiClient.post(`/api/viva/instructor/course/${this.courseId}/assignments/create/`, assignmentData);
          if (response.data.assignment_id) {
            this.assignment.id = response.data.assignment_id;
            this.isNewAssignment = false;
          }
          if (response.data.updated_at) {
            this.originalUpdatedAt = response.data.updated_at;
          }
        } else {
          // Include original_updated_at for concurrency check
          if (this.originalUpdatedAt) {
            assignmentData.original_updated_at = this.originalUpdatedAt;
          }
          const response = await apiClient.put(`/api/viva/instructor/assignment/${this.assignment.id}/update/`, assignmentData);
          if (response.data.updated_at) {
            this.originalUpdatedAt = response.data.updated_at;
          }
        }

        // Load students with their actual accommodations before opening modal
        await this.loadStudentsWithAccommodations();

        // Open the modal to review student list
        if (reviewStudents){
          this.showStudentModal = true;
        } else {
          alert('Assignment saved successfully');
        }

      } catch (error) {
        console.error('Failed to save assignment:', error);

        // Handle 409 conflict
        if (error.response && error.response.status === 409) {
          alert('This assignment was modified by another instructor. Please reload the page to refresh with the latest changes.');
          return;
        }

        const errorMsg = handleApiError(error, this.$router, 'Failed to save assignment');
        if (error.response && error.response.status !== 401) {
          this.errors.general = errorMsg;
          alert(errorMsg);
        }
      } finally {
        this.saving = false;
      }
    },

    closeStudentModal() {
      this.showStudentModal = false;
    },

    async loadStudentsWithAccommodations() {
      // If assignment exists, try to load students with their actual accommodations from submissions
      if (this.assignment.id) {
        try {
          const response = await apiClient.get(`/api/viva/instructor/assignment/${this.assignment.id}/submissions/`);
          if (response.data && response.data.submissions && response.data.submissions.length > 0) {
            // Use submissions data which has actual accommodation values
            this.studentList = response.data.submissions.map(sub => ({
              email: sub.student_email,
              text_questions_enabled: sub.text_questions_enabled || false,
              time_multiplier: parseFloat(sub.time_multiplier) || 1.00,
              isNew: false
            }));
            return;
          }
        } catch (error) {
          console.log('No submissions found, using course students with defaults');
        }
      }

      // Fall back to course students with default accommodations
      // (this.studentList was already populated by loadCourse())
    },

    parseEmailsToStudentList(emailsStr) {
      // Parse comma/space/newline separated emails into student objects
      if (!emailsStr || !emailsStr.trim()) return [];

      const emails = emailsStr.split(/[,;\s\n]+/).filter(e => e.trim());
      return emails.map(email => ({
        email: email.trim().toLowerCase(),
        text_questions_enabled: false,
        time_multiplier: 1.00,
        isNew: false
      }));
    },

    addBulkStudents() {
      if (!this.bulkAddEmails.trim()) return;

      const newEmails = this.bulkAddEmails.split(/[,;\s\n]+/).filter(e => e.trim());
      const existingEmails = new Set(this.studentList.map(s => s.email.toLowerCase()));

      for (const email of newEmails) {
        const cleanEmail = email.trim().toLowerCase();
        if (cleanEmail && !existingEmails.has(cleanEmail)) {
          this.studentList.push({
            email: cleanEmail,
            text_questions_enabled: false,
            time_multiplier: 1.00,
            isNew: true
          });
          existingEmails.add(cleanEmail);
        }
      }

      this.bulkAddEmails = '';
      this.showBulkAddInput = false;
    },

    removeStudent(index) {
      this.studentList.splice(index, 1);
    },

    toggleExtraTime(index) {
      const student = this.studentList[index];
      // Toggle between 1.0 and 1.5
      student.time_multiplier = student.time_multiplier > 1 ? 1.00 : 1.50;
    },

    async saveStudentListAndSend() {
      this.sending = true;

      try {
        // Build email list and accommodations map from studentList
        const validStudents = this.studentList.filter(s => s.email && s.email.trim());
        const recipientEmails = validStudents.map(s => s.email.trim().toLowerCase());

        // Only add new students to the course roster — don't pass the full list,
        // which would (via update_course) remove students who were removed from
        // this send list but should remain enrolled in the course.
        const newStudents = validStudents.filter(s => s.isNew);
        if (newStudents.length > 0) {
          const newEmails = newStudents.map(s => s.email.trim().toLowerCase()).join(', ');
          await apiClient.put(`/api/viva/instructor/course/${this.courseId}/update/`, {
            student_emails: newEmails
          });
        }

        // Build accommodations map for send
        const studentAccommodations = {};
        validStudents.forEach(s => {
          studentAccommodations[s.email.trim().toLowerCase()] = {
            text_questions_enabled: s.text_questions_enabled,
            time_multiplier: parseFloat(s.time_multiplier)
          };
        });

        // Send the assignment with explicit recipient list and accommodations
        const sendData = {
          send_now: true,
          student_accommodations: studentAccommodations,
          recipient_emails: recipientEmails,
          notify_students: this.notifyStudents,
          
        };
        if (this.originalUpdatedAt) {
          sendData.original_updated_at = this.originalUpdatedAt;
        }
        const response = await apiClient.put(`/api/viva/instructor/assignment/${this.assignment.id}/update/`, sendData);

        if (response.data.updated_at) {
          this.originalUpdatedAt = response.data.updated_at;
        }

        if (response.data.failed_emails && response.data.failed_emails.length > 0) {
          alert(`Assignment sent, but ${response.data.failed_emails.length} email(s) failed.`);
        }

        // Close modal and go back
        this.showStudentModal = false;
        this.goBack();

      } catch (error) {
        console.error('Failed to save student list and send:', error);

        // Handle 409 conflict
        if (error.response && error.response.status === 409) {
          alert('This assignment was modified by another instructor. Please reload the page to refresh with the latest changes.');
          return;
        }

        const errorMsg = handleApiError(error, this.$router, 'Failed to send assignment');
        if (error.response && error.response.status !== 401) {
          alert(errorMsg);
        }
      } finally {
        this.sending = false;
      }
    },

    validate() {
      this.errors = {};
      let isValid = true;

      // Validate title
      if (!this.assignment.title || this.assignment.title.trim().length === 0) {
        this.errors.title = 'Assignment title is required';
        isValid = false;
      }

      // Validate due date
      if (!this.dueDateLocal) {
        this.errors.due_date = 'Due date is required';
        isValid = false;
      } else {
        // Check that due date is in the future
        const dueDate = new Date(this.dueDateLocal);
        const now = new Date();
        if (dueDate <= now) {
          this.errors.due_date = 'Due date must be in the future';
          isValid = false;
        }
      }

      // Validate customization fields
      if (this.isCustomized) {
        if (this.answerTime < 5 || this.answerTime > 300 || !Number.isInteger(this.answerTime)) {
          this.errors.answerTime = 'Answer time must be a whole number between 5 and 300 seconds';
          isValid = false;
        }
        const hasCustomFirst = this.customFirstEnabled;
        const hasCustomLast = this.customLastEnabled;
        const minRequired = 1 + (hasCustomFirst ? 1 : 0) + (hasCustomLast ? 1 : 0);
        if (this.numQuestions < minRequired) {
          this.errors.numQuestions = `With your selected options you need at least ${minRequired} total questions`;
          isValid = false;
        }
        if (hasCustomFirst && !this.customFirstQuestion.trim()) {
          this.errors.customFirstQuestion = 'Please enter the first question';
          isValid = false;
        }
        if (hasCustomLast && !this.customLastQuestion.trim()) {
          this.errors.customLastQuestion = 'Please enter the last question';
          isValid = false;
        }
      }

      // Validate teacher upload - file must be uploaded if teacher mode selected
      if (this.uploadMode === 'teacher') {
        if (this.uploadState === 'uploading') {
          this.errors.upload = 'Please wait for the file upload to complete';
          isValid = false;
        } else if (!this.assignedFileId) {
          this.errors.upload = 'Please upload a file for the assignment';
          isValid = false;
        }
      }

      return isValid;
    },

    cancel() {
      this.goBack();
    },

    goBack() {
      if (this.courseId) {
        this.$router.push({
          name: 'course-assignments',
          params: { id: this.courseId }
        });
      } else {
        this.$router.push({ name: 'instructor-dashboard' });
      }
    },

    resetInstructions() {
      if (this.uploadMode === 'teacher') {
        this.assignment.instructions = DEFAULT_INSTRUCTIONS_TEACHER_UPLOAD;
      } else {
        this.assignment.instructions = DEFAULT_INSTRUCTIONS;
      }
    },

    handleTeacherFileUpload(event) {
      if (this.uploadState === 'uploading') {
        console.log('Upload already in progress, ignoring duplicate event');
        return;
      }

      console.log('Teacher file upload event:', event);

      if (event.error) {
        this.uploadError = event.error;
        return;
      }

      if (event.fileData && event.file) {
        this.uploadState = 'uploading';
        this.uploadError = null;
        this.uploadProgress = 0;

        // Show filename of upload
        this.assignedFileName = event.file.name;

        // Check filename length and truncate if over 90 characters for the actual upload
        let fileToUpload = event.file;
        if (event.file.name.length > 90) {
          const extension = event.file.name.split('.').pop();
          const nameWithoutExtension = event.file.name.substring(0, event.file.name.lastIndexOf('.'));
          const maxNameLength = 90 - extension.length - 1;
          const truncatedName = nameWithoutExtension.substring(0, maxNameLength) + '.' + extension;
          fileToUpload = new File([event.file], truncatedName, { type: event.file.type });
        }

        // Use chunkUpload from ivChunkUploadMixin
        this.chunkUpload(
          "/tus_files/",
          fileToUpload,
          this,
          "uploadProgress",
          this.onTeacherUploadComplete,
          this.onTeacherUploadError
        );
      } else if (event.fileData === null) {
        // File was removed
        this.removeAssignedFile();
      }
    },

    onTeacherUploadComplete(resource_id) {
      console.log('Teacher upload completed, resource_id:', resource_id);
      this.assignedFileId = resource_id;
      this.assignedFileInfo.fileUrl = `/video/v1/file/${resource_id}`;
      this.uploadState = 'complete';
    },

    onTeacherUploadError(error) {
      console.error('Teacher upload failed:', error);
      this.uploadError = 'Upload failed: ' + (error.message || 'Unknown error');
      this.uploadState = 'initial';
      this.uploadProgress = 0;
    },

    removeAssignedFile() {
      this.assignedFileId = null;
      this.assignedFileName = '';
      this.assignedFileInfo.fileUrl = null;
      this.uploadState = 'initial';
      this.uploadProgress = 0;
      this.uploadError = null;
    }
  },

  watch: {
    uploadMode(newMode) {
      // Update default instructions when upload mode changes
      if (newMode === 'teacher') {
        if (this.assignment.instructions === DEFAULT_INSTRUCTIONS) {
          this.assignment.instructions = DEFAULT_INSTRUCTIONS_TEACHER_UPLOAD;
        }
      } else {
        if (this.assignment.instructions === DEFAULT_INSTRUCTIONS_TEACHER_UPLOAD) {
          this.assignment.instructions = DEFAULT_INSTRUCTIONS;
        }
      }
    }
  }
};
</script>

<style scoped>
.instructor-info {
  margin-bottom: 0;
  padding-bottom: 0;
  padding-top: 5px !important;
}

.course-info-text {
  text-transform: none;
  font-family: 'Playfair Display', 'Times New Roman', Times, serif;
}

.viva-form-container {
  max-width: 1200px;
  margin: 0;
  margin-left: 30px;
  padding-top: 15px;
}

.form-left {
  padding: 10px
}

.form-right {
  padding-left: 20px;
  border-left: 2px solid var(--viva-dark);
}

.or-divider-with-lines {
  display: flex;
  align-items: center;
  border-top: 2px solid var(--viva-grey);
  border-bottom: 2px solid var(--viva-grey);
  gap: 10px;
  margin: 20px 0;
  color: var(--viva-grey);
  font-weight: 600;
  font-size: 18px;
}

.customized-options {
  position: relative;
}

.customized-options.active .custom-option-label {
  cursor: default;
  color: var(--viva-dark);
}

.custom-question-input:not(:disabled) {
  border-color: var(--viva-dark);
  opacity: 1;
}

.custom-option-number-input {
  width: 52px;
  height: 36px;
  border: 2px solid var(--viva-grey);
  border-radius: 20%;
  text-align: center;
  font-size: 18px;
  font-weight: 600;
  color: var(--viva-dark);
  flex-shrink: 0;
  padding: 0;
}

.custom-option-number-input:not(:disabled) {
  border-color: var(--viva-dark);
  cursor: text;
}

/* Student List Modal - Wider variant */
.student-list-modal {
  max-width: 800px;
  width: 90%;
}

/* Bulk Add Section */
.bulk-add-section {
  margin-bottom: 20px;
}

.bulk-add-form {
  margin-top: 15px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.bulk-add-form .form-textarea {
  min-height: 80px;
}

/* Student Table */
.student-table-wrapper {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid var(--viva-green);
  border-radius: 8px;
}

.student-list-table {
  width: 100%;
  border-collapse: collapse;
}

.student-list-table thead {
  background-color: var(--viva-green);
  color: white;
  position: sticky;
  top: 0;
}

.student-list-table th {
  padding: 12px 10px;
  text-align: left;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.student-list-table td {
  padding: 10px;
  border-bottom: 1px solid #eee;
}

.student-list-table tbody tr:hover {
  background-color: var(--viva-cream);
}

.email-col {
  min-width: 250px;
}

.accommodation-col {
  min-width: 120px;
  text-align: center;
}

.action-col {
  width: 40px;
  text-align: center;
}

/* Form Input Variants */
.form-input-small {
  padding: 8px 10px;
  font-size: 14px;
  width: 100%;
  border: 1px solid var(--viva-green);
  border-radius: 4px;
}

.form-select-small {
  padding: 6px 8px;
  font-size: 14px;
  border: 1px solid var(--viva-green);
  border-radius: 4px;
  background: white;
}

/* Toggle Switch */
.toggle-switch {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
}

.toggle-switch input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: var(--viva-green);
}

.toggle-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--viva-grey);
}

/* Accommodation Toggle Button */
.accommodation-btn {
  padding: 6px 14px;
  border-radius: 6px;
  border: 2px solid #6c757d;
  background-color: #f8f9fa;
  color: #495057;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.accommodation-btn:hover {
  background-color: #e2e6ea;
  border-color: #5a6268;
}

.accommodation-btn.active {
  background-color: var(--viva-green);
  border-color: var(--viva-green);
  color: white;
}

.accommodation-btn.active:hover {
  background-color: #5a9f5f;
  border-color: #5a9f5f;
}

/* Empty State */
.empty-table-message {
  padding: 40px 20px;
  text-align: center;
  color: var(--viva-grey);
}

/* Student Count */
.student-count {
  margin-top: 15px;
  font-size: 14px;
  color: var(--viva-green);
  font-weight: 600;
}

/* Upload Mode Toggle Switch */
.upload-toggle-switch {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  width: 100%;
}

.upload-toggle-switch div {
  padding: 0;
}

/* The switch - the box around the slider */
.switch {
  position: relative;
  display: inline-block;
  width: 70px;
  height: 34px;
}

/* Hide default HTML checkbox */
.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

/* The slider */
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--viva-orange);
  -webkit-transition: 0.4s;
  transition: 0.4s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 26px;
  width: 26px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  -webkit-transition: 0.4s;
  transition: 0.4s;
}

input:checked + .slider {
  background-color: var(--viva-green);
}

input:focus + .slider {
  box-shadow: 0 0 1px var(--viva-green);
}

input:disabled + .slider {
  opacity: 0.5;
  cursor: not-allowed;
}

input:checked + .slider:before {
  -webkit-transform: translateX(10px);
  -ms-transform: translateX(10px);
  transform: translateX(10px);
}

/* Rounded sliders */
.slider.round {
  border-radius: 34px;
}

.slider.round:before {
  border-radius: 50%;
}

.point {
  cursor: pointer;
}

/* Teacher Upload Section */
.teacher-upload-section {
  margin-top: 15px;
  padding: 15px;
  background-color: var(--viva-cream);
  border-radius: 8px;
  border: 1px solid #ddd;
}

.upload-progress {
  text-align: center;
  padding: 20px;
  color: var(--viva-green);
  font-weight: 500;
}

.upload-progress .fa-spinner {
  margin-right: 10px;
  font-size: 18px;
}

.uploaded-file-display {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px;
  background-color: white;
  border: 2px solid var(--viva-green);
  border-radius: 8px;
}

.uploaded-file-display .fa-file-text-o {
  color: var(--viva-green);
  font-size: 24px;
}

.uploaded-file-display span {
  flex: 1;
  font-weight: 500;
  color: var(--viva-dark);
  word-break: break-all;
}

.download-file-btn {
  color: var(--viva-green);
  padding: 4px 8px;
  margin-right: 8px;
  text-decoration: none;
}

.download-file-btn:hover {
  color: var(--viva-dark);
}

.remove-file-btn {
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 20px;
  font-weight: bold;
  line-height: 1;
}

.remove-file-btn:hover {
  background-color: #f8d7da;
}

.upload-error {
  color: #dc3545;
  font-size: 14px;
  margin-top: 10px;
  font-weight: 500;
}

.notify-students-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  align-self: center;
  color: var(--viva-grey);
}

.notify-students-label input[type="checkbox"] {
  accent-color: var(--viva-orange);
}

</style>
